import { FC } from 'react';
import { useWatch, Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import NewOrderSummaryEmpty from '@/features/orders/components/NewOrder/NewOrderSummary/NewOrderSummaryEmpty';
import NewOrderSummarySkeleton from '@/features/orders/components/NewOrder/NewOrderSummary/NewOrderSummarySkeleton';
import NewOrderSummaryElement from './NewOrderSummaryElement';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import CurrencyValue from '@/features/finances/components/CurrencyValue';
import { calculateOrderTotal } from '@/features/orders/utils/orderPricingUtils';
import type { NewOrderFormData } from '@/features/orders/validators/NewOrderValidator';

type ServiceEntriesProps = {
	name: string;
	price: number;
	quantity: number;
};

type Props = {
	control: Control<NewOrderFormData>;
	isLoading?: boolean;
	isError?: boolean;
};

const NewOrderSummary: FC<Props> = ({
	control,
	isLoading = false,
	isError = false,
}) => {
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();

	const services = useWatch({ control, name: 'lines' }) || {};
	const serviceId = useWatch({ control, name: 'serviceId' });
	const hasService = !!serviceId?.[0]?.id;
	const servicePrice = hasService ? (serviceId?.[0]?.price ?? 0) : 0;

	const serviceEntries: ServiceEntriesProps[] = Object.values(services);

	const orderTotal = calculateOrderTotal(serviceEntries, false, servicePrice);
	const showServiceFee = hasService;

	const total = <CurrencyValue value={orderTotal} asset='other' />;

	return (
		<div className='shrink-0 flex flex-col gap-4'>
			<div className='flex flex-col gap-2.5'>
				<h3 className={cn(isMobile && 'text-base')}>
					{t(
						'main.orders.components.create.components.summary.title',
					)}
				</h3>
			</div>
			<div
				className={cn(
					'p-6 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10',
					isMobile && 'p-0 grid-cols-2 border-0 bg-popover-30',
				)}>
				{(isLoading || isError) && <NewOrderSummarySkeleton />}
				{!isLoading && hasService && (
					<>
						{serviceEntries.map((service, index) => (
							<NewOrderSummaryElement
								key={index}
								{...service}
							/>
						))}
						{showServiceFee && (
							<div className='flex justify-between text-sm text-popover-800'>
								<span>
									{t(
										'main.orders.components.create.components.summary.serviceFee',
									)}
								</span>
								<CurrencyValue
									value={servicePrice}
									asset='other'
								/>
							</div>
						)}
						<div className='flex justify-between text-base font-medium'>
							<span>
								{t(
									'main.orders.components.create.components.summary.total',
								)}
							</span>
							<span>{total}</span>
						</div>
					</>
				)}
				{!isLoading && !hasService && <NewOrderSummaryEmpty />}
			</div>
		</div>
	);
};

export default NewOrderSummary;
